/* variant-url-fix.js — Cleveland Equipment / Foxecom Hyper
 * Server bug: section render returns the DEFAULT variant for any requested variant.
 * This forces correct variant state client-side and SUPPRESSES the theme's broken section
 * fetch on tap (no flicker), updates price/SKU/Model and per-variant CONTENT regions,
 * re-notifies Globo, and keeps URL-landing + tapping consistent.
 *
 * v11:
 *   - PRICE          : Shopify-faithful formatMoney (thousands grouping) — no flash.
 *   - #1 SKU         : #product__sku <- v.sku.
 *   - #2 Model       : [data-selected-swatch-value="Model"] <- v.option1.
 *   - #3 Globo       : non-bubbling change on the hidden product-form [name=id] input.
 *   - CONTENT (short description / long "Details" description / specifications):
 *       These are server-rendered metafield/tab regions, NOT in product.js or the variant
 *       object. We LAZY-LOAD them: on variant select, fetch that variant's full product page
 *       (which renders correctly even though the section AJAX is broken), extract each region
 *       in CONTENT_SWAP_IDS, swap them in, and CACHE by variant id. One fetch per variant
 *       covers all regions; re-selecting a cached variant is instant. The landing variant is
 *       seeded from the page on load (no fetch). Product-level regions simply get the same
 *       content back (harmless no-op).
 *
 * v12:
 *   - PARTIAL instant content. Previously an entry in #variant-content-map short-circuited
 *     the fetch entirely, so a map covering only SOME ids would have silently frozen the
 *     rest (description/specs) on their landing-variant content. The map is now MERGED into
 *     the cache and applied immediately, and the lazy fetch still runs whenever any id in
 *     CONTENT_SWAP_IDS is still missing for that variant.
 *   - This lets cheap regions render with zero latency while expensive ones keep lazy-loading.
 *     `product__origin_badges` (origin-badge.liquid) is the first such region: a few dozen
 *     bytes per variant, emitted for every variant up-front, so badges no longer wait ~1s on
 *     a full product-page fetch.
 *
 * Band-aid; real fix is a Shopify ticket.
 */
(function () {
  var PV = null;            // product variants
  var userInteracted = false;
  var moneyFormat = (window.Shopify && Shopify.money_format) || '${{amount}}';
  var currentVariantId = null;

  /* ----------------------------------------------------------------------
   * CONTENT CONFIG
   * Element ids whose innerHTML is per-variant and should be swapped from the
   * fetched variant page. Confirmed differing per variant on the SGP filler:
   *   - product__short_description        (short description / spec summary)
   *   - block-specs-accordion-content     (Specifications tab)
   *   - block-description-accordion-content (Details / long description)  [included per request;
   *       if this is product-level on a given product, the swap is a harmless no-op]
   *   - product__origin_badges            (Made in / Assembled in badges; served instantly
   *                                        from #variant-content-map, see v12 above)
   * Remove any id you don't want swapped. Product-level tabs (Downloads, Videos) are omitted.
   * -------------------------------------------------------------------- */
  var CONTENT_SWAP_IDS = [
    'product__short_description',
    'block-description-accordion-content',
    'block-specs-accordion-content',
    'product__origin_badges'
  ];

  // Lazy fetch fallback. Set false to disable per-deployment.
  var CONTENT_FETCH_ENABLED = true;

  var CONTENT_CACHE = {};       // variantId -> { elementId: html, ... }
  var CONTENT_FETCHING = {};    // variantId -> in-flight guard

  function norm(s) { return String(s).replace(/\s+/g, ' ').trim(); }
  function getWantId() { return new URLSearchParams(location.search).get('variant'); }

  function currentSelectedId() {
    var w = getWantId();
    if (w) return w;
    var inp = document.querySelector("product-info input[name='id']");
    return inp ? inp.value : null;
  }

  /* Shopify-faithful money formatting (mirrors the theme's own formatMoney). */
  function formatMoney(cents) {
    var fmt = moneyFormat;
    var ph = /\{\{\s*(\w+)\s*\}\}/;

    function withDelims(number, precision, thousands, decimal) {
      precision = (precision == null) ? 2 : precision;
      thousands = (thousands == null) ? ',' : thousands;
      decimal = (decimal == null) ? '.' : decimal;
      if (number == null || isNaN(number)) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? (decimal + parts[1]) : '';
      return dollars + rest;
    }

    var match = fmt.match(ph);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount':                                    value = withDelims(cents, 2); break;
      case 'amount_no_decimals':                        value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator':               value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':   value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':          value = withDelims(cents, 2, "'"); break;
      case 'amount_with_space_separator':               value = withDelims(cents, 2, ' '); break;
      case 'amount_no_decimals_with_space_separator':   value = withDelims(cents, 0, ' '); break;
      default:                                          value = withDelims(cents, 2);
    }
    return fmt.replace(ph, value);
  }

  function loadVariants(cb) {
    if (PV) { cb && cb(); return; }
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      var t = scripts[i].textContent;
      if (!t) continue;
      try {
        var d = JSON.parse(t);
        var vs = d.variants || (d.product && d.product.variants);
        if (vs && vs.length && vs[0].option1 !== undefined) { PV = vs; break; }
      } catch (e) {}
    }
    if (PV) { cb && cb(); return; }
    fetch(location.pathname + '.js').then(function (r) { return r.json(); })
      .then(function (p) { PV = p.variants; cb && cb(); }).catch(function () {});
  }

  function variantById(id) { return PV && PV.find(function (v) { return String(v.id) === String(id); }); }
  function variantByLabel(label) { return PV && PV.find(function (v) { return norm(v.option1) === norm(label); }); }

  function updatePrice(v) {
    if (!v) return;
    var el = document.querySelector("[id^='price-']");
    if (!el) return;
    var regular = el.querySelector('.f-price-item--regular');
    if (regular) {
      regular.textContent = formatMoney(v.price);
    }
  }

  /* Fallback "Label: value" patcher — only used if the stable hooks below are missing. */
  function setLabeled(labelRe, newVal) {
    if (newVal == null || newVal === '') return false;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = walker.nextNode())) {
      var t = n.nodeValue;
      if (!t) continue;
      var pe = n.parentElement;
      if (!pe || pe.closest('script,style,noscript')) continue;
      var m = t.match(labelRe);
      if (!m) continue;
      var after = t.slice(m.index + m[0].length);
      if (after.trim()) { n.nodeValue = t.slice(0, m.index + m[0].length) + ' ' + newVal; return true; }
      var sibEl = pe.nextElementSibling;
      if (sibEl && sibEl.textContent.trim()) { sibEl.textContent = newVal; return true; }
      var sibNode = pe.nextSibling;
      while (sibNode && sibNode.nodeType === 3 && !sibNode.nodeValue.trim()) sibNode = sibNode.nextSibling;
      if (sibNode && sibNode.nodeType === 3) { sibNode.nodeValue = ' ' + newVal; return true; }
    }
    return false;
  }

  function updateSku(v) {                                   // #1
    if (!v || v.sku == null) return;
    var el = document.getElementById('product__sku');
    if (el) { el.textContent = v.sku; return; }
    setLabeled(/SKU\s*#?\s*:/i, v.sku);                     // fallback
  }

  function updateModel(v) {                                 // #2
    if (!v) return;
    var el = document.querySelector('[data-selected-swatch-value="Model"]')
          || document.querySelector('[data-selected-swatch-value]');
    if (el) { el.textContent = v.option1; return; }
    setLabeled(/Model\s*:/i, v.option1);                    // fallback
  }

  /* ----------------------------------------------------------------------
   * PER-VARIANT CONTENT (instant map + lazy fetch + cache)
   * -------------------------------------------------------------------- */

  // Instant source, may cover ONLY SOME ids:
  //   <script type="application/json" id="variant-content-map">
  //     { "<variantId>": { "<elementId>": "<html>", ... }, ... }
  //   </script>
  // Whatever it provides is painted with no network round-trip; anything it
  // omits still goes through the lazy fetch below.
  function getInstantContent(id) {
    var el = document.getElementById('variant-content-map');
    if (!el) return null;
    try { var m = JSON.parse(el.textContent); return (m && m[id]) ? m[id] : null; }
    catch (e) { return null; }
  }

  // True only when every swap id is accounted for (a null value counts: it means
  // "checked, absent on that page"). Drives whether a fetch is still needed.
  function isComplete(obj) {
    if (!obj) return false;
    for (var i = 0; i < CONTENT_SWAP_IDS.length; i++) {
      if (!(CONTENT_SWAP_IDS[i] in obj)) return false;
    }
    return true;
  }

  function applyContent(id) {
    var obj = CONTENT_CACHE[String(id)];
    if (!obj) return;
    CONTENT_SWAP_IDS.forEach(function (elId) {
      if (obj[elId] == null) return;
      var el = document.getElementById(elId);
      if (el) el.innerHTML = obj[elId];
    });
  }

  function fetchVariantContent(v) {
    var id = String(v.id);
    if (isComplete(CONTENT_CACHE[id])) { applyContent(id); return; }   // nothing missing
    if (CONTENT_FETCHING[id]) return;                                   // in flight
    CONTENT_FETCHING[id] = true;

    var url = location.pathname + '?variant=' + encodeURIComponent(id);
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        CONTENT_FETCHING[id] = false;
        if (!html) return;
        var doc;
        try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return; }
        var obj = CONTENT_CACHE[id] || {};
        CONTENT_SWAP_IDS.forEach(function (elId) {
          if (obj[elId] != null) return;                 // keep what the instant map gave us
          var src = doc.getElementById(elId);
          obj[elId] = src ? src.innerHTML : null;
        });
        CONTENT_CACHE[id] = obj;
        if (String(currentVariantId) === id) applyContent(id);   // still selected?
      })
      .catch(function () { CONTENT_FETCHING[id] = false; });
  }

  function updateVariantContent(v) {
    if (!v) return;
    var id = String(v.id);

    var instant = getInstantContent(id);
    if (instant) {
      var merged = CONTENT_CACHE[id] || {};
      Object.keys(instant).forEach(function (k) { merged[k] = instant[k]; });
      CONTENT_CACHE[id] = merged;
      applyContent(id);                                   // paint immediately, no network
    }

    if (isComplete(CONTENT_CACHE[id])) { applyContent(id); return; }
    if (CONTENT_FETCH_ENABLED) fetchVariantContent(v);    // fill in whatever is still missing
  }

  function captureInitialContent() {
    var id = currentSelectedId();
    if (id == null) return;
    var obj = CONTENT_CACHE[String(id)] || {};
    CONTENT_SWAP_IDS.forEach(function (elId) {
      var el = document.getElementById(elId);
      obj[elId] = el ? el.innerHTML : null;
    });
    CONTENT_CACHE[String(id)] = obj;                        // seed: landing variant, no fetch
  }

  /* #3 Globo — wake via the event it already listens for (non-bubbling change on the
   * hidden product-form [name=id] input = Globo's variantSelector). */
  function notifyGlobo(v) {
    if (!v) return;
    var G = window.GPOConfigs;
    var sel = G && G.theme && G.theme.product && G.theme.product.variantSelector;
    if (!sel) return;
    var el = document.querySelector(sel);
    if (!el) return;
    try {
      if ('value' in el) el.value = v.id;
      el.dispatchEvent(new Event('change', { bubbles: false }));
    } catch (e) {}
  }

  function forceVariant(v) {
    if (!v) return;
    currentVariantId = String(v.id);
    document.querySelectorAll('input[name="id"]').forEach(function (inp) {
      if (inp.closest('product-info')) inp.value = v.id;
    });
    document.querySelectorAll('variant-selects input[type=radio]').forEach(function (r) {
      r.checked = (norm(r.value) === norm(v.option1));
    });
    if (getWantId() !== String(v.id)) {
      history.replaceState({}, '', location.pathname + '?variant=' + v.id);
    }
    updatePrice(v);            // price
    updateSku(v);              // #1
    updateModel(v);            // #2
    updateVariantContent(v);   // short desc / long desc / specs (lazy) + badges (instant)
    notifyGlobo(v);            // #3 Globo
  }

  /* ---- Tap handling: intercept BEFORE the theme, force state, suppress its broken fetch ---- */
  function onCaptureChange(e) {
    var radio = e.target;
    if (!radio || radio.type !== 'radio' || !radio.closest('variant-selects')) return;

    userInteracted = true;

    var v = variantByLabel(radio.value);
    if (!v) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    forceVariant(v);
  }

  function hookTaps() {
    document.addEventListener('change', onCaptureChange, true);
  }

  /* ---- URL landing: force the requested variant on load, stand down on first tap ---- */
  function initUrlLanding() {
    var want = getWantId();
    if (!want) return;

    var apply = function () {
      if (userInteracted) return;
      var v = variantById(want);
      if (v) forceVariant(v);
    };

    apply();
    [0, 100, 300, 600, 1000, 1500, 2500].forEach(function (d) { setTimeout(apply, d); });
    document.addEventListener('matchMobile', apply);
    document.addEventListener('unmatchMobile', apply);

    var pi = document.querySelector('product-info');
    if (pi && 'MutationObserver' in window) {
      var obs = new MutationObserver(function () {
        if (userInteracted) { obs.disconnect(); return; }
        var v = variantById(want);
        var inp = document.querySelector("product-info input[name='id']");
        if (v && inp && String(inp.value) !== String(v.id)) forceVariant(v);
      });
      obs.observe(pi, { childList: true, subtree: true });
      setTimeout(function () { obs.disconnect(); }, 4000);
    }
  }

  function run() {
    loadVariants(function () {
      captureInitialContent();
      hookTaps();
      initUrlLanding();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();